import React, { useState, useEffect } from 'react';
import { useMessage } from '../../contexts/MessageContext';
import { useApi } from '../../utils/api';
import { PageTitleBar } from '../../components/PageTitleBar';

interface Organization {
  name: string;
  checked: boolean;
}

export default function EmailAnalytics() {
  const { message, setMessage } = useMessage();
  const { fetchWithAuth } = useApi();
  
  // Calculate default dates
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  };
  
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  };
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate());
  const [recipientEmails, setRecipientEmails] = useState<string>('');
  const [reportType, setReportType] = useState<'summary' | 'detailed'>('summary');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalStatus, setModalStatus] = useState<'success' | 'error'>('success');
  const [modalMessage, setModalMessage] = useState<string>('');
  const [reportDetails, setReportDetails] = useState<{
    recipients: string[];
    organizations: string[];
    period: string;
  } | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      setIsLoading(true);
      try {
        const response = await fetchWithAuth('/admin/organizations');
        
        if (!response.ok) {
          throw new Error('Failed to fetch organizations');
        }
        
        const data = await response.json();
        const orgs = data.organizations.map((org: string) => ({
          name: org,
          checked: false
        }));
        
        setOrganizations(orgs);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setMessage('Failed to load organizations. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOrganizations();
  }, [fetchWithAuth, setMessage]);

  const handleOrganizationChange = (orgName: string) => {
    setOrganizations(organizations.map(org => 
      org.name === orgName ? { ...org, checked: !org.checked } : org
    ));
  };

    const closeModal = () => {
    setShowModal(false);
  };

  // Email validation helper
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateEmails = (emailString: string): { isValid: boolean; invalidEmails: string[] } => {
    const emails = emailString.split(',').map(email => email.trim()).filter(email => email.length > 0);
    const invalidEmails = emails.filter(email => !validateEmail(email));
    return {
      isValid: emails.length > 0 && invalidEmails.length === 0,
      invalidEmails
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const selectedOrgs = organizations.filter(org => org.checked).map(org => org.name);
    
    // Check at least one organization is selected
    if (selectedOrgs.length === 0) {
      setMessage('Please select at least one organization');
      return;
    }
    
    // Check dates are present
    if (!startDate || !endDate) {
      setMessage('Please select both start and end dates');
      return;
    }
    
    // Check end date is after start date
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end <= start) {
      setMessage('End date must be after start date');
      return;
    }
    
    // Check recipient emails
    if (!recipientEmails) {
      setMessage('Please enter at least one recipient email');
      return;
    }
    
    // Validate email addresses
    const emailValidation = validateEmails(recipientEmails);
    if (!emailValidation.isValid) {
      if (emailValidation.invalidEmails.length > 0) {
        setMessage(`Invalid email address(es): ${emailValidation.invalidEmails.join(', ')}`);
      } else {
        setMessage('Please enter at least one valid email address');
      }
      return;
    }
    
    // Prepare data for submission with JSON array for organizations
    const payload = {
      start_date: startDate,
      end_date: endDate,
      organizations: selectedOrgs,
      recipient_email: recipientEmails,
      report_type: reportType
    };
    
    setIsSubmitting(true);
    
    try {
      const response = await fetchWithAuth('/admin/email-blast-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to send report');
      }
      
      const result = await response.json();
      
      // Show success modal
      setModalStatus('success');
      setModalMessage(result.message || 'Email blast report sent successfully');
      setReportDetails({
        recipients: result.recipients || recipientEmails.split(',').map(email => email.trim()),
        organizations: result.organizations || selectedOrgs,
        period: result.period || `${startDate} - ${endDate}`
      });
      setShowModal(true);
      
      // Reset form
      setOrganizations(organizations.map(org => ({ ...org, checked: false })));
      setStartDate(getDefaultStartDate());
      setEndDate(getDefaultEndDate());
      setRecipientEmails('');
    } catch (error) {
      console.error('Error sending report:', error);
      
      // Show error modal
      setModalStatus('error');
      setModalMessage('Failed to send report. Please try again.');
      setReportDetails(null);
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {message && (
        <div className="mb-4 p-4 bg-[#8bcfc2] border border-[#3eaf99] text-[#0b677c] rounded">
          {message}
        </div>
      )}
      
      <PageTitleBar
        title="Email Analytics"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Email Analytics' },
        ]}
      />
      
      <div className="bg-white rounded-sm shadow-md p-6">
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-panagora-teal"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-panagora-slate-gray mb-3">Organization(s)</h2>
              <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
                {organizations.map(org => (
                  <div key={org.name} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`org-${org.name}`}
                      checked={org.checked}
                      onChange={() => handleOrganizationChange(org.name)}
                      className="h-4 w-4 text-panagora-teal focus:ring-panagora-teal border-gray-300 rounded"
                    />
                    <label htmlFor={`org-${org.name}`} className="ml-2 block text-sm text-gray-700">
                      {org.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-panagora-slate-gray mb-3">Blast Date Range</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="start-date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-panagora-teal focus:border-panagora-teal"
                  />
                </div>
                <div>
                  <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    id="end-date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-panagora-teal focus:border-panagora-teal"
                  />
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-panagora-slate-gray mb-3">Report Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    reportType === 'summary' 
                      ? 'border-panagora-teal bg-panagora-teal bg-opacity-5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setReportType('summary')}
                >
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      name="reportType"
                      value="summary"
                      checked={reportType === 'summary'}
                      onChange={(e) => setReportType(e.target.value as 'summary' | 'detailed')}
                      className="mt-1 h-4 w-4 text-panagora-teal focus:ring-panagora-teal border-gray-300"
                    />
                    <div className="ml-3">
                      <span className="font-medium text-gray-900 block mb-1">Summary Report</span>
                      <p className="text-sm text-gray-600">High-level overview grouped by organization and email subject</p>
                    </div>
                  </label>
                </div>
                
                <div 
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    reportType === 'detailed' 
                      ? 'border-panagora-teal bg-panagora-teal bg-opacity-5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setReportType('detailed')}
                >
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      name="reportType"
                      value="detailed"
                      checked={reportType === 'detailed'}
                      onChange={(e) => setReportType(e.target.value as 'summary' | 'detailed')}
                      className="mt-1 h-4 w-4 text-panagora-teal focus:ring-panagora-teal border-gray-300"
                    />
                    <div className="ml-3">
                      <span className="font-medium text-gray-900 block mb-1">Detailed Report</span>
                      <p className="text-sm text-gray-600">Complete event timeline with all recipient interactions</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-panagora-slate-gray mb-3">Send Report to:</h2>
              <label htmlFor="recipient-emails" className="block text-sm font-medium text-gray-700 mb-1">
                Email Addresses (comma separated)
              </label>
              <input
                type="text"
                id="recipient-emails"
                value={recipientEmails}
                onChange={(e) => setRecipientEmails(e.target.value)}
                placeholder="example@mail.com, another@mail.com"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-panagora-teal focus:border-panagora-teal"
              />
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 rounded-md font-medium text-white bg-panagora-teal hover:bg-panagora-teal-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-panagora-teal ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Sending...' : 'Send Report'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${modalStatus === 'success' ? 'text-panagora-teal' : 'text-red-600'}`}>
                {modalStatus === 'success' ? 'Report Sent Successfully' : 'Report Failed'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-700">{modalMessage}</p>
            </div>
            
            {modalStatus === 'success' && reportDetails && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-500">Report Period</p>
                  <p className="text-sm text-gray-900">{reportDetails.period}</p>
                </div>
                
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-500">Organizations</p>
                  <p className="text-sm text-gray-900">{reportDetails.organizations.join(', ')}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Recipients</p>
                  <p className="text-sm text-gray-900">{reportDetails.recipients.join(', ')}</p>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-panagora-teal text-white rounded-md hover:bg-panagora-teal-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-panagora-teal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 